import { useCallback, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useLabelSuggestion } from 'modules/timesheet-labels/hooks';
import { useTimesheetLabelSuggestionContext } from 'modules/timesheet-labels';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import {
  LabelSuggestionsInput,
  SuggestionLabelValueDetailsInput,
  SuggestionLabelValueSource,
  TimeSignalAttribute,
  LabelType,
  SuggestionLabelValueAcceptedBy,
  InputMaybe
} from 'graphql/types';
import {
  CustomLabel,
  TimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import { NOTE_FIELDS } from '../../common/noteSchema';

export const mapFormValuesToSuggestionsInput = (
  values: {
    [p: string]: any;
  },
  attributes: TimeSignalAttribute[] | undefined,
  labelsFormField: string,
  customLabelField: string
): LabelSuggestionsInput => {
  const validLabelsForSuggestion = (values[labelsFormField] as Array<{
    label: TimesheetLabel;
  }>).filter((item) => ![LabelType.Comment].includes(item.label.type));

  const inputTimeSheetLabels = validLabelsForSuggestion.map<SuggestionLabelValueDetailsInput>(
    ({ label }) => ({
      labelId: label.id,
      value: label.value,
      valueSource: (label.valueSource as unknown) as SuggestionLabelValueSource,
      acceptedBy: (label.acceptedBy ??
        null) as InputMaybe<SuggestionLabelValueAcceptedBy>
    })
  );
  const inputCustomLabels = (values[customLabelField] ?? []).map(
    (label: CustomLabel) => {
      return {
        labelId: label.id,
        value: label.value,
        valueSource: (label.valueSource as unknown) as SuggestionLabelValueSource,
        acceptedBy: (label.acceptedBy ??
          null) as InputMaybe<SuggestionLabelValueAcceptedBy>
      };
    }
  );

  const inputLables = [...inputTimeSheetLabels, ...inputCustomLabels];

  const commentLabel = (values[labelsFormField] as Array<{
    label: TimesheetLabel;
  }>).find((item) => item.label.type === LabelType.Comment);
  return {
    date: values[NOTE_FIELDS.DATE]
      ? DateTime.fromJSDate(values[NOTE_FIELDS.DATE]).toUTC().toISODate()
      : undefined,
    startTime: values[NOTE_FIELDS.START_TIME]
      ? DateTime.fromJSDate(values[NOTE_FIELDS.START_TIME]).toUTC().toISO()
      : undefined,
    endTime: values[NOTE_FIELDS.END_TIME]
      ? DateTime.fromJSDate(values[NOTE_FIELDS.END_TIME]).toUTC().toISO()
      : undefined,
    duration: values[NOTE_FIELDS.DURATION],
    labelValues: inputLables,
    inferenceId: values[NOTE_FIELDS.LABEL_VAL_INF_ID],
    comment: commentLabel?.label.value?.stringValue,
    description: values[NOTE_FIELDS.NOTE],
    attributes: attributes
      ? attributes.map((attribute) => ({
          name: attribute.name,
          value: attribute.value,
          sourceId: attribute.sourceId
        }))
      : undefined
  };
};

export type FetchSuggestionsHandlerProps = {
  attributes?: TimeSignalAttribute[] | undefined;
  description?: string | undefined;
  hasSuggestionStartTime?: boolean;
  hasSuggestionEndTime?: boolean;
  hasSuggestionDuration?: boolean;
  handleClose?: () => void;
};
export type FetchSuggestionsHandler = (
  props: FetchSuggestionsHandlerProps
) => void;

export const useFetchSuggestions = (): FetchSuggestionsHandler => {
  const { getValues, setValue } = useFormContext();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();

  const {
    getLabelSuggestions,
    labelSuggestionData,
    loading
  } = useLabelSuggestion();
  const { setLabelSuggestions } = useTimesheetLabelSuggestionContext();

  const debounced = useDebouncedCallback((input?: LabelSuggestionsInput) => {
    getLabelSuggestions(input);
  }, 1000);

  const fetchSuggestions = useCallback(
    ({
      description,
      attributes,
      hasSuggestionStartTime,
      hasSuggestionEndTime,
      hasSuggestionDuration
    }: FetchSuggestionsHandlerProps) => {
      if (isSmartSuggestionEnabled) {
        const values = getValues();

        const labelSuggestionsInput = mapFormValuesToSuggestionsInput(
          {
            ...values,
            [NOTE_FIELDS.NOTE]:
              description === undefined ? values.note : description,
            [NOTE_FIELDS.START_TIME]: hasSuggestionStartTime
              ? null
              : values[NOTE_FIELDS.START_TIME],
            [NOTE_FIELDS.END_TIME]: hasSuggestionEndTime
              ? null
              : values[NOTE_FIELDS.END_TIME],
            [NOTE_FIELDS.DURATION]: hasSuggestionDuration
              ? null
              : values[NOTE_FIELDS.DURATION]
          },
          attributes ?? [],
          NOTE_FIELDS.TIMESHEET_LABELS,
          NOTE_FIELDS.CUSTOM_LABELS
        );

        debounced(labelSuggestionsInput);
      }
    },
    [isSmartSuggestionEnabled, getValues, debounced]
  );
  useEffect(() => {
    if (loading) return;
    if (!labelSuggestionData) {
      setLabelSuggestions(undefined);
    } else {
      setLabelSuggestions(labelSuggestionData);
      setValue(
        NOTE_FIELDS.LABEL_VAL_INF_ID,
        labelSuggestionData?.labelSuggestions?.inferenceId,
        { shouldDirty: false }
      );
    }
  }, [setLabelSuggestions, labelSuggestionData, loading, setValue]);

  return fetchSuggestions;
};

export default useFetchSuggestions;
